import { useAgentChat } from "@cloudflare/ai-chat/react";
import { useAgent } from "agents/react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { decodeHtmlEntities } from "../../shared/text";
import type { OnlineOperator, SupportState } from "../../shared/support";
import { parseOnlineTeam } from "../../shared/online-team";
import { OnlineTeam } from "./OnlineTeam";
import { formatTakeoverDelay, resolveTakeoverDelay } from "../../shared/support-settings";
import { conversationIdentity, conversationStarters } from "./conversation-ui";
import type {
  ProductCard,
  ProductCategoryLink,
  ProductCategorySuggestion,
  PublicSiteConfig,
} from "../../shared/types";

interface EmbedConfig {
  site: PublicSiteConfig;
  agentName: string;
  conversationId: string;
  pageUrl: string;
  pageTitle: string;
}

export function EmbedApp() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") ?? "", []);
  const [config, setConfig] = useState<EmbedConfig | null>(null);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/public/config?token=${encodeURIComponent(token)}`, { signal: controller.signal })
      .then(async (response) => {
        const body = await response.json() as EmbedConfig & { error?: string };
        if (!response.ok) throw new Error(body.error ?? "Unable to load the assistant");
        setConfig(body);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setProblem(error instanceof Error ? error.message : "Unable to load the assistant");
      });
    return () => controller.abort();
  }, [token]);

  if (problem) return <EmbedProblem message={problem} />;
  if (!config) return <div className="embed-loading" aria-label="Loading assistant"><span /></div>;
  return <Conversation config={config} token={token} />;
}

function Conversation({ config, token }: { config: EmbedConfig; token: string }) {
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("open") === "1");
  const [input, setInput] = useState("");
  const [productContext, setProductContext] = useState("");
  const [supportState, setSupportState] = useState<SupportState>({ support: null });
  const [onlineTeam, setOnlineTeam] = useState<OnlineOperator[]>([]);
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const [page, setPage] = useState({ pageUrl: config.pageUrl, pageTitle: config.pageTitle });
  const listRef = useRef<HTMLDivElement>(null);
  const agent = useAgent({
    agent: "ChatAgent",
    name: config.agentName,
    query: { token },
    onStateUpdate: (state: SupportState) => setSupportState(state),
  });
  const { messages, sendMessage, status, error, stop } = useAgentChat({
    agent,
    body: page,
  });
  const busy = status === "submitted" || status === "streaming";

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      let origin = "";
      try { origin = new URL(config.pageUrl).origin; } catch { return; }
      if (event.source !== window.parent || event.origin !== origin) return;
      if (event.data?.type === "woo-chat:team") setOnlineTeam(parseOnlineTeam(event.data.operators, window.location.origin));
      if (event.data?.type === "woo-chat:open") {
        setOpen(true);
        if (typeof event.data.productName === "string" && event.data.productName.trim()) {
          const name = event.data.productName.trim().slice(0, 200);
          setProductContext(name);
          // Context is visible but no message is sent until the shopper chooses to send.
          if (event.data.prefill === true) setInput((draft) => draft || `I have a question about ${name}. `);
        }
      }
      if (event.data?.type === "woo-chat:page" && typeof event.data.pageUrl === "string" && typeof event.data.pageTitle === "string") {
        setPage({ pageUrl: event.data.pageUrl.slice(0, 2_048), pageTitle: event.data.pageTitle.slice(0, 200) });
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [config.pageUrl]);

  useEffect(() => {
    window.parent.postMessage({ type: "woo-chat:resize", open }, "*");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, [messages, open]);

  const hasCustomerMessage = messages.some((message) => message.role === "user");

  function track(event: "started" | "product_clicked") {
    // keepalive preserves a product-click event when the link navigates the top page.
    void fetch("/api/public/engagement", {
      method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
      body: JSON.stringify({ token, events: [event] }),
    }).catch(() => { /* Analytics never prevents a message or navigation. */ });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    await sendMessage({ text });
    track("started");
  }

  async function selectCategory(category: ProductCategorySuggestion) {
    if (busy) return;
    const categoryName = decodeHtmlEntities(category.name);
    setInput("");
    await sendMessage({
      text: `Show me ${categoryName}`,
      metadata: { selectedCategory: { name: categoryName, slug: category.slug } },
    });
    track("started");
  }

  async function selectStarter(text: string) {
    if (busy) return;
    setInput("");
    await sendMessage({ text });
    track("started");
  }

  const theme = { "--chat-primary": config.site.primaryColor } as React.CSSProperties;
  const identity = conversationIdentity(supportState.support, config.site.assistantName);
  const photoUrl = supportState.support?.photoUrl;
  if (!open) {
    return (
      <button className="chat-launcher" style={theme} onClick={() => setOpen(true)} aria-label={`Open ${config.site.assistantName}`}>
        <ChatIcon />
      </button>
    );
  }

  return (
    <section className="chat-window" style={theme} aria-label={config.site.assistantName}>
      <header className="chat-header">
        <div className="assistant-avatar">{photoUrl && photoUrl !== failedPhoto ? <img src={photoUrl} alt={identity.name} onError={() => setFailedPhoto(photoUrl)} /> : <SparkIcon />}</div>
        <div>
          <strong>{identity.name}</strong>
          <span>{identity.label}</span>
        </div>
        <button className="icon-button" onClick={() => setOpen(false)} aria-label="Close product assistant"><CloseIcon /></button>
        <OnlineTeam operators={onlineTeam} />
      </header>

      <div className="chat-messages" ref={listRef} aria-live="polite">
        <div className="message-row assistant-row">
          <div className="message-bubble assistant-bubble">{config.site.greeting}</div>
        </div>
        {productContext && <p className="chat-product-context">About: {productContext}</p>}
        {!hasCustomerMessage && <div className="chat-starters" aria-label="Suggested questions">
          <p>Choose a question, or type your own.</p>
          {conversationStarters(productContext).map((starter) => <button type="button" key={starter.label} disabled={busy} onClick={() => void selectStarter(starter.text)}>{starter.label}</button>)}
        </div>}
        {messages.map((message) => (
          <Message
            key={message.id}
            message={message}
            categorySelectionDisabled={busy || Boolean(supportState.support)}
            onCategorySelect={selectCategory}
            onProductClick={() => track("product_clicked")}
          />
        ))}
        {status === "submitted" && <div className="typing" aria-label="Assistant is thinking"><span /><span /><span /></div>}
        {supportState.support?.replyDueAt && <p className="chat-handoff-notice" role="status">We’ve notified {supportState.support.name}. If they can’t reply within {formatTakeoverDelay(resolveTakeoverDelay(supportState.support.replyTimeoutSeconds))}, our AI assistant will help.</p>}
        {error && <p className="chat-error">Something interrupted that answer. Please try again.</p>}
      </div>

      <form className="chat-composer" onSubmit={submit}>
        <label className="sr-only" htmlFor="chat-input">Ask about products</label>
        <textarea
          id="chat-input"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          rows={1}
          maxLength={1_500}
          placeholder="What are you looking for?"
          disabled={busy}
        />
        {busy ? (
          <button type="button" className="send-button" onClick={() => stop()} aria-label="Stop response"><StopIcon /></button>
        ) : (
          <button type="submit" className="send-button" disabled={!input.trim()} aria-label="Send message"><SendIcon /></button>
        )}
      </form>
      <p className="chat-disclaimer">{identity.notice}</p>
    </section>
  );
}

function Message({
  message,
  categorySelectionDisabled,
  onCategorySelect,
  onProductClick,
}: {
  message: { id: string; role: string; parts: readonly unknown[]; metadata?: unknown };
  categorySelectionDisabled: boolean;
  onCategorySelect: (category: ProductCategorySuggestion) => Promise<void>;
  onProductClick: () => void;
}) {
  const products: ProductCard[] = [];
  const categories: ProductCategorySuggestion[] = [];
  const text: string[] = [];
  let categorySuggestionsReturned = false;
  let browseCategory: ProductCategoryLink | null = null;

  for (const part of message.parts) {
    if (!part || typeof part !== "object" || !("type" in part)) continue;
    if (part.type === "text" && "text" in part && typeof part.text === "string") text.push(part.text);
    if (
      (part.type === "tool-searchProducts"
        || part.type === "tool-getProductDetails"
        || part.type === "tool-browseProductCategory")
      && "output" in part
    ) {
      products.push(...productsFromOutput(part.output));
      if (part.type === "tool-browseProductCategory") {
        browseCategory = categoryLinkFromOutput(part.output);
      }
    }
    if (part.type === "tool-suggestProductCategories" && "output" in part) {
      categorySuggestionsReturned = true;
      categories.push(...categoriesFromOutput(part.output));
    }
  }

  if (!text.length && !products.length && !categorySuggestionsReturned) return null;
  const assistant = message.role === "assistant";
  const humanName = message.metadata && typeof message.metadata === "object" && "humanAdmin" in message.metadata && typeof message.metadata.humanAdmin === "string" ? message.metadata.humanAdmin : "";
  return (
    <div className={`message-row ${assistant ? "assistant-row" : "user-row"}`}>
      {humanName && <small className="human-message-label">{humanName} · Store team</small>}
      {text.map((value, index) => (
        <div className={`message-bubble ${assistant ? "assistant-bubble" : "user-bubble"}`} key={index}>
          {assistant ? decodeHtmlEntities(value) : value}
        </div>
      ))}
      {categorySuggestionsReturned && (
        <CategoryChoices
          categories={uniqueCategories(categories)}
          disabled={categorySelectionDisabled}
          onSelect={onCategorySelect}
        />
      )}
      {products.length > 0 && <ProductRail products={uniqueProducts(products)} category={browseCategory} onProductClick={onProductClick} />}
    </div>
  );
}

function CategoryChoices({
  categories,
  disabled,
  onSelect,
}: {
  categories: ProductCategorySuggestion[];
  disabled: boolean;
  onSelect: (category: ProductCategorySuggestion) => Promise<void>;
}) {
  if (categories.length === 0) {
    return <div className="message-bubble assistant-bubble">Could you be a little more specific about the type of product you need?</div>;
  }
  return (
    <div className="category-choices">
      <strong>Which type are you interested in?</strong>
      <div>
        {categories.map((category) => (
          <button
            type="button"
            disabled={disabled}
            onClick={() => void onSelect(category)}
            key={category.slug || category.name}
          >
            <span>{decodeHtmlEntities(category.name)}</span><i aria-hidden="true">›</i>
          </button>
        ))}
      </div>
    </div>
  );
}

function ProductRail({ products, category, onProductClick }: { products: ProductCard[]; category: ProductCategoryLink | null; onProductClick: () => void }) {
  return (
    <div className="product-rail">
      {products.map((product) => {
        const productName = decodeHtmlEntities(product.name);
        return (
          <a className="product-card" href={product.permalink} target="_top" rel="noopener noreferrer" key={product.id} onClick={onProductClick}>
            <div className="product-image-wrap">
              {product.images[0]?.thumbnail || product.images[0]?.src
                ? <img src={product.images[0].thumbnail || product.images[0].src} alt={decodeHtmlEntities(product.images[0].alt || productName)} loading="lazy" />
                : <div className="product-placeholder"><BagIcon /></div>}
            </div>
            <div className="product-info">
              <strong>{productName}</strong>
              <span>{decodeHtmlEntities(product.formattedPrice || product.price)}</span>
              <small>{product.stockStatus === "instock" ? "In stock" : product.stockStatus.replaceAll("-", " ")}</small>
            </div>
            <ArrowIcon />
          </a>
        );
      })}
      {category && (
        <a className="category-view-all" href={category.url} target="_top" rel="noopener noreferrer">
          <span>See all {decodeHtmlEntities(category.name)}</span>
          <ArrowIcon />
        </a>
      )}
    </div>
  );
}

function productsFromOutput(output: unknown): ProductCard[] {
  if (!output || typeof output !== "object" || !("products" in output) || !Array.isArray(output.products)) return [];
  return output.products.filter((product): product is ProductCard => Boolean(
    product && typeof product === "object" && "id" in product && "name" in product && "permalink" in product,
  ));
}

function categoriesFromOutput(output: unknown): ProductCategorySuggestion[] {
  if (!output || typeof output !== "object" || !("categories" in output) || !Array.isArray(output.categories)) return [];
  return output.categories.filter((category): category is ProductCategorySuggestion => Boolean(
    category
      && typeof category === "object"
      && "name" in category
      && typeof category.name === "string"
      && "slug" in category
      && typeof category.slug === "string",
  ));
}

function categoryLinkFromOutput(output: unknown): ProductCategoryLink | null {
  if (!output || typeof output !== "object" || !("category" in output)) return null;
  const category = output.category;
  if (!category || typeof category !== "object") return null;
  if (!("name" in category) || typeof category.name !== "string") return null;
  if (!("slug" in category) || typeof category.slug !== "string") return null;
  if (!("url" in category) || typeof category.url !== "string") return null;
  return { name: category.name, slug: category.slug, url: category.url };
}

function uniqueProducts(products: ProductCard[]): ProductCard[] {
  return Array.from(new Map(products.map((product) => [product.id, product])).values()).slice(0, 8);
}

function uniqueCategories(categories: ProductCategorySuggestion[]): ProductCategorySuggestion[] {
  return Array.from(new Map(categories.map((category) => [category.slug || category.name, category])).values()).slice(0, 6);
}

function EmbedProblem({ message }: { message: string }) {
  return <div className="embed-problem"><strong>Assistant unavailable</strong><span>{message}</span></div>;
}

function ChatIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11.4a7.5 7.5 0 0 1-8 7.1 9.6 9.6 0 0 1-3-.5L4 20l1.6-4a7 7 0 0 1-1.6-4.6 7.5 7.5 0 0 1 8-7.1 7.5 7.5 0 0 1 8 7.1Z"/><path d="M8 11.5h.01M12 11.5h.01M16 11.5h.01"/></svg>; }
function SparkIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 1.1 4.1a5 5 0 0 0 3.6 3.6L21 12l-4.3 1.3a5 5 0 0 0-3.6 3.6L12 21l-1.1-4.1a5 5 0 0 0-3.6-3.6L3 12l4.3-1.3a5 5 0 0 0 3.6-3.6L12 3Z"/></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>; }
function SendIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 4 17 8-17 8 3-8-3-8Z"/><path d="M7 12h14"/></svg>; }
function StopIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="1"/></svg>; }
function ArrowIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }
function BagIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 8h14l-1 12H6L5 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg>; }
